<template>
  <div class="min-h-screen bg-neutral-50 py-8">
    <div class="max-w-5xl mx-auto px-4">
      <!-- 加载中 -->
      <div v-if="loading" class="text-center py-24 text-neutral-400">
        <i class="fa fa-spinner fa-spin text-3xl"></i>
        <p class="mt-4">正在加载工具…</p>
      </div>

      <!-- 不存在 -->
      <div v-else-if="!tool" class="text-center py-24">
        <i class="fa fa-folder-open-o text-4xl text-neutral-300"></i>
        <p class="mt-4 text-neutral-500">未找到该工具，可能已被删除</p>
        <router-link
          to="/"
          class="inline-block mt-4 text-primary hover:text-primary-dark"
          >返回首页</router-link
        >
      </div>

      <div v-else>
        <!-- ===== 工具头部 ===== -->
        <div class="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          <div class="flex flex-col md:flex-row gap-6">
            <img
              v-if="tool.cover"
              :src="tool.cover"
              :alt="tool.title"
              class="w-full md:w-52 h-40 md:h-40 object-cover rounded-xl bg-neutral-100 flex-shrink-0"
            />
            <div class="flex-1 min-w-0">
              <h1 class="text-2xl font-bold text-neutral-900">
                {{ tool.title }}
              </h1>

              <div
                class="flex flex-wrap items-center gap-3 mt-3 text-sm text-neutral-500"
              >
                <span v-if="categoryName"
                  ><i class="fa fa-folder-o mr-1"></i>{{ categoryName }}</span
                >
                <span
                  ><i class="fa fa-eye mr-1"></i>{{ tool.viewCount || 0 }}
                  次浏览</span
                >
                <span
                  ><i class="fa fa-download mr-1"></i
                  >{{ tool.downloadCount || 0 }} 次下载</span
                >
                <span v-if="tool.userName"
                  ><i class="fa fa-user-o mr-1"></i>{{ tool.userName }}</span
                >
              </div>

              <p
                v-if="tool.description"
                class="mt-4 text-neutral-600 leading-relaxed"
              >
                {{ tool.description }}
              </p>

              <!-- 操作按钮 -->
              <div class="flex flex-wrap gap-3 mt-6">
                <button
                  v-if="tool.fileType && (tool.fileType == 'html' || tool.fileType == 'zip')"
                  @click="preAction()"
                  :disabled="!playEnabled"
                  class="px-6 py-3 rounded-xl bg-primary text-white font-medium hover:bg-primary/90 apple-transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="playEnabled ? '' : '平台已关闭在线互动'"
                >
                  <i class="fa fa-play-circle mr-1"></i>
                  {{ playEnabled ? "在线互动" : "在线互动已关闭" }}
                </button>

                <button
                  @click="downloadAction()"
                  :disabled="!downloadEnabled"
                  class="px-6 py-3 rounded-xl border border-neutral-300 text-neutral-700 font-medium hover:border-primary hover:text-primary apple-transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="downloadEnabled ? '' : '平台已关闭下载'"
                >
                  <i class="fa fa-download mr-1"></i>
                  {{ downloadEnabled ? "下载工具" : "下载已关闭" }}
                </button>

                <button
                  @click="shareAction()"
                  class="px-6 py-3 rounded-xl border border-neutral-300 text-neutral-700 font-medium hover:border-primary hover:text-primary apple-transition"
                >
                  <i class="fa fa-share-alt mr-1"></i> 分享
                </button>

                <button
                  @click="favAction()"
                  class="px-4 py-3 rounded-xl border border-neutral-300 apple-transition"
                  :class="
                    tool.isFav
                      ? 'text-red-500 border-red-200'
                      : 'text-neutral-700 hover:text-red-500'
                  "
                  title="收藏工具"
                >
                  <i class="fa" :class="tool.isFav ? 'fa-heart' : 'fa-heart-o'"></i>
                </button>

                <button
                  @click="zanAction()"
                  class="px-4 py-3 rounded-xl border border-neutral-300 apple-transition"
                  :class="
                    tool.isZan
                      ? 'text-primary border-primary/30'
                      : 'text-neutral-700 hover:text-primary'
                  "
                  title="点赞工具"
                >
                  <i class="fa" :class="tool.isZan ? 'fa-thumbs-up' : 'fa-thumbs-o-up'"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== 用户评价 ===== -->
        <div class="bg-white rounded-2xl shadow-sm p-6 md:p-8 mt-6">
          <h2 class="text-xl font-bold mb-6">用户评价</h2>

          <!-- 评分输入区（下载过的用户才能评价） -->
          <div
            class="mb-8 p-4 bg-neutral-100 rounded-xl"
            v-if="tool.hasDownLoad && tool.status == 1"
          >
            <h3 class="font-medium mb-3">分享你的使用体验</h3>
            <div class="flex items-center mb-4">
              <div class="flex space-x-1">
                <i
                  v-for="n in 5"
                  :key="n"
                  class="fa text-xl cursor-pointer apple-transition"
                  :class="
                    n <= userRating
                      ? 'fa-star text-yellow-400'
                      : 'fa-star-o text-neutral-300 hover:text-yellow-400'
                  "
                  @click="userRating = n"
                ></i>
              </div>
              <span class="ml-3 text-neutral-500 text-sm">点击星星评分</span>
            </div>
            <textarea
              v-model="commentText"
              rows="3"
              placeholder="分享你对这个工具的使用感受和建议..."
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none apple-transition"
            ></textarea>
            <div class="mt-4 flex justify-end">
              <button
                @click="submitComment"
                class="px-6 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 apple-transition"
              >
                提交评论
              </button>
            </div>
          </div>

          <!-- 评论列表 -->
          <div v-if="commentList.length == 0" class="text-center py-8 text-neutral-400">
            暂无评价，快来抢沙发吧
          </div>
          <div v-else class="space-y-6">
            <div v-for="item in commentList" :key="item.id" class="flex items-start">
              <img
                :src="item.avatar"
                alt="头像"
                class="w-9 h-9 rounded-full mr-3 bg-neutral-200"
              />
              <div class="flex-1">
                <div class="flex justify-between items-center mb-1">
                  <h4 class="font-medium">{{ item.uname }}</h4>
                  <span class="text-xs text-neutral-500">{{
                    fmtTime(item.createTime)
                  }}</span>
                </div>
                <div class="flex mb-2 space-x-1">
                  <i
                    v-for="n in 5"
                    :key="n"
                    class="fa text-sm"
                    :class="
                      n <= item.rate
                        ? 'fa-star text-yellow-400'
                        : 'fa-star-o text-neutral-300'
                    "
                  ></i>
                </div>
                <p class="text-neutral-600 text-sm">{{ item.content }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLogin } from "@/utils/authGuard";
import {
  getDownloadCost,
  isDownloadEnabled,
  isPlayEnabled,
} from "@/utils/pointConfig";
import { isToolFree } from "@/utils/downloadRule";

export default {
  name: "ToolPage",
  data() {
    return {
      loading: true,
      tool: null,
      userRating: 5,
      commentText: "",
      page: 1,
      total: 0,
      commentList: [],
      categoryName: "",
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
    downloadCost() {
      return getDownloadCost();
    },
    downloadEnabled() {
      return isDownloadEnabled();
    },
    playEnabled() {
      return isPlayEnabled();
    },
    toolIsFree() {
      return isToolFree(this.tool, this.isLogin ? this.user.uid : "");
    },
  },
  created() {
    this.getTool();
  },
  methods: {
    fmtTime(t) {
      if (!t) return "";
      const d = new Date(t);
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
        d.getDate()
      )} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },

    getTool() {
      const id = this.$route.params.id;
      if (!id) {
        this.loading = false;
        return;
      }
      this.loading = true;
      let uid = this.isLogin ? this.user.uid : "";
      this.$axios({
        url: `aitool/${id}?uid=${uid}`,
      })
        .then(async (res) => {
          this.tool = res.data || null;
          this.loading = false;
          if (this.tool) {
            document.title = this.tool.title + " - 小义AI教育工具平台";
            await this.getComments();
            this.loadCategoryName();
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    loadCategoryName() {
      if (!this.tool || !this.tool.categoryid) return;
      this.$axios({ url: "category/list" })
        .then((res) => {
          const list = (res && res.data) || [];
          const hit = list.find((c) => c.id === this.tool.categoryid);
          if (hit) this.categoryName = hit.title;
        })
        .catch(() => {});
    },

    async getComments() {
      const data = await this.$aesEncrypt({ page: this.page, pageSize: 10 });
      this.$axios({
        url: "aitool/user/comment/list/" + this.tool.id,
        method: "POST",
        data: { data: data },
      }).then((res) => {
        if (res.code == 200) {
          this.total = res.data.total;
          this.commentList = res.data.list || [];
        }
      });
    },

    /** 在线互动：跳转到独立的在线使用页 */
    preAction() {
      if (!requireLogin(this, "在线使用工具")) return;
      if (!isPlayEnabled()) {
        ElMessage({
          message: "平台已关闭在线互动权限，暂无法使用",
          type: "warning",
        });
        return;
      }
      this.$router.push({ path: "/play/" + this.tool.id, query: { ppt: "1" } });
    },

    downloadAction() {
      if (!requireLogin(this, "下载文件")) return;
      if (!isDownloadEnabled()) {
        ElMessage({
          message: "平台已关闭下载权限，暂无法下载",
          type: "warning",
        });
        return;
      }
      const doDownload = () => {
        this.$axios({ url: "aitool/download/" + this.tool.id }).then((res) => {
          if (res.code == 200) {
            this.downloadFile(res.data.file, this.tool.title);
            ElMessage({ message: "下载已开始", type: "success" });
          } else {
            ElMessage({ message: res.msg || "下载失败", type: "error" });
          }
        });
      };
      if (this.tool.hasDownLoad) {
        doDownload();
        return;
      }
      ElMessageBox.confirm(
        this.toolIsFree
          ? `确认免费下载工具《${this.tool.title}》`
          : `确认消耗：${this.downloadCost}创值下载工具《${this.tool.title}》`,
        "下载工具",
        { confirmButtonText: "下载", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => doDownload())
        .catch(() => {});
    },

    downloadFile(fileUrl, fileName) {
      fetch(fileUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });
    },

    /** 分享：复制当前工具的专属页面链接 */
    async shareAction() {
      const url = window.location.origin + "/tool/" + this.tool.id;
      const name = this.tool.title || "AI 教学工具";
      // 仅在移动端唤起系统分享：桌面端部分环境的 share() 会挂起，导致点了没反应
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Mobile/i.test(
        navigator.userAgent
      );
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: name,
            text: "分享一个好用的教学工具：" + name,
            url: url,
          });
          return;
        } catch (e) {
          /* 用户取消则继续走复制 */
        }
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        ElMessage({
          message: "链接已复制，发给同事即可直接打开该工具",
          type: "success",
        });
      } catch (e) {
        ElMessage({
          message: "复制失败，请手动复制：" + url,
          type: "warning",
          duration: 6000,
        });
      }
    },

    favAction() {
      if (!requireLogin(this, "收藏工具")) return;
      if (this.tool.isFav) {
        this.tool.isFav = false;
        this.$axios({
          url: "aitool/action/fav/" + this.tool.id,
          method: "DELETE",
        }).then((res) => {
          if (res.code != 200) ElMessage.error(res.msg);
        });
      } else {
        this.tool.isFav = true;
        this.$axios({ url: "aitool/action/fav/" + this.tool.id }).then((res) => {
          if (res.code != 200) ElMessage.error(res.msg);
        });
      }
    },

    zanAction() {
      if (!requireLogin(this, "点赞工具")) return;
      if (this.tool.isZan) {
        this.tool.isZan = false;
        this.$axios({
          url: "aitool/action/zan/" + this.tool.id,
          method: "DELETE",
        }).then((res) => {
          if (res.code != 200) ElMessage.error(res.msg);
        });
      } else {
        this.tool.isZan = true;
        this.$axios({ url: "aitool/action/zan/" + this.tool.id }).then((res) => {
          if (res.code != 200) ElMessage.error(res.msg);
        });
      }
    },

    async submitComment() {
      if (!requireLogin(this, "发表评论")) return;
      if (!this.commentText.trim()) {
        ElMessage({ message: "请输入评论内容", type: "warning" });
        return;
      }
      const data = await this.$aesEncrypt({
        toolId: this.tool.id,
        rate: this.userRating,
        content: this.commentText,
      });
      this.$axios({
        url: "aitool/user/comment",
        method: "PUT",
        data: { data: data },
      }).then(async (res) => {
        if (res.code == 200) {
          ElMessage.info("提交评论成功");
          this.commentText = "";
          this.userRating = 5;
          await this.getComments();
        } else {
          ElMessage.error(res.msg);
        }
      });
    },
  },
};
</script>
